import { useState } from 'react';
import { useAdminUsers, useAddUser, useUpdateUser } from './useAdminUsers';
import type { AdminUser } from '@/types/api';
import { useCurrentUser } from '@/features/auth/useCurrentUser';
import { Avatar } from '@/app/layouts/Sidebar';
import { Skeleton, SkeletonGroup } from '@/components/Skeleton';
import { Dropdown } from '@/components/Dropdown';

/**
 * The roster.
 *
 * There is no delete. Cards reference their assignee and the context log outlives the
 * person, so removing someone would either orphan their history or destroy it.
 * Offboarding is Deactivate, which locks them out on their next request and leaves the
 * record intact.
 */

const STATUS_STYLE: Record<AdminUser['status'], { label: string; color: string }> = {
  active: { label: 'Active', color: 'var(--color-status-completed)' },
  invited: { label: 'Invited', color: 'var(--color-status-pending)' },
  suspended: { label: 'Deactivated', color: 'var(--color-status-cancelled)' },
};

export function AdminScreen() {
  const { data: me } = useCurrentUser();
  const users = useAdminUsers();
  const add = useAddUser();
  const update = useUpdateUser();

  /*
    `AdminUser['role']`, not `'employee' as const`.

    The old shape typed this as the literal 'employee' and the <select> cast its way
    past it — so picking Admin set the state to 'admin' at runtime while TypeScript
    believed it could not be. It worked, but nothing was checking it.
  */
  const [form, setForm] = useState<{
    fullName: string;
    email: string;
    telegramUserId: string;
    role: AdminUser['role'];
  }>({ fullName: '', email: '', telegramUserId: '', role: 'employee' });
  const [error, setError] = useState<string | null>(null);



  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    add.mutate(
      {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        telegramUserId: form.telegramUserId.trim() || null,
        role: form.role,
      },
      {
        onSuccess: () => setForm({ fullName: '', email: '', telegramUserId: '', role: 'employee' }),
        onError: (err) => setError(err.message),
      },
    );
  }

  const field = 'w-full rounded-lg border px-2.5 py-2 text-sm';
  const fieldStyle = {
    borderColor: 'var(--border)',
    background: 'var(--surface-raised)',
    color: 'var(--text)',
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-3"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
      >
        <h1 className="text-base font-semibold tracking-tight">People</h1>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Who the desks can assign work to
          {users.data && ` · ${users.data.items.length}`}
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar">
      <div className="mx-auto flex max-w-3xl flex-col gap-6 p-4 sm:p-6">
        <section
          className="rounded-2xl border p-4"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
        >
          <h2 className="text-sm font-semibold">Add someone</h2>
          <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            They can be assigned work straight away. Their board appears the first time
            they sign in with this exact address.
          </p>

          <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Full name
              <input
                required value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className={`mt-1 ${field}`} style={fieldStyle} placeholder="Mallikarjun"
              />
            </label>

            <label className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Work email — must match their Google account
              <input
                required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={`mt-1 ${field}`} style={fieldStyle} placeholder="name@thetapacademy.com"
              />
            </label>

            <label className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Telegram id — how the desks address them
              <input
                inputMode="numeric" value={form.telegramUserId}
                onChange={(e) => setForm({ ...form, telegramUserId: e.target.value })}
                className={`mt-1 ${field}`} style={fieldStyle} placeholder="5829173461 (optional)"
              />
            </label>

            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Role
              <div className="mt-1">
                {/*
                  The hint line is the reason this is not a native select. What "admin"
                  MEANS here is the thing people get wrong, and an OS-drawn popup cannot
                  show a second line to say it.
                */}
                <Dropdown
                  label="Role"
                  placeholder="Employee"
                  value={form.role}
                  options={[
                    {
                      value: 'employee' as const,
                      label: 'Employee',
                      hint: 'Sees only the cards assigned to them',
                    },
                    {
                      value: 'admin' as const,
                      label: 'Admin',
                      hint: 'Sees every card, and manages this list',
                      color: 'var(--accent)',
                    },
                  ]}
                  onChange={(role) => setForm({ ...form, role: role ?? 'employee' })}
                />
              </div>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                type="submit" disabled={add.isPending}
                className="min-h-11 rounded-xl px-4 text-sm font-medium disabled:opacity-60"
                style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
              >
                {add.isPending ? 'Adding…' : 'Add to the roster'}
              </button>
              {error && (
                <p className="text-xs" style={{ color: 'var(--color-status-pending)' }}>{error}</p>
              )}
            </div>
          </form>
        </section>

        <section>
          <h2 className="mb-2 px-1 text-sm font-semibold">
            Roster{' '}
            <span className="font-normal" style={{ color: 'var(--text-muted)' }}>
              {users.data?.items.length}
            </span>
          </h2>

          {users.isPending && (
            <SkeletonGroup label="Loading the roster" className="flex flex-col gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border p-3"
                  style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                >
                  <Skeleton width={32} height={32} radius={999} />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton width="35%" height={12} />
                    <Skeleton width="60%" height={10} />
                  </div>
                  <Skeleton width={64} height={10} />
                </div>
              ))}
            </SkeletonGroup>
          )}

          <div className="flex flex-col gap-2">
            {users.data?.items.map((user) => {
              const status = STATUS_STYLE[user.status];
              const isSelf = user.id === me?.id;

              return (
                <div
                  key={user.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border p-3"
                  style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                >
                  <Avatar name={user.fullName || user.email} url={null} size={32} />

                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      {user.fullName || user.email}
                      {user.role === 'admin' && (
                        <span
                          className="rounded-full px-1.5 py-0.5 text-[10px]"
                          style={{ background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}
                        >
                          admin
                        </span>
                      )}
                      {isSelf && (
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>you</span>
                      )}
                    </p>
                    <p className="mt-0.5 flex flex-wrap gap-x-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      <span>{user.email}</span>
                      {user.telegramUserId
                        ? <span className="font-mono">tg {user.telegramUserId}</span>
                        : <span>no Telegram id</span>}
                      <span>{user.openTasks} open</span>
                      {/* Distinguishes "added but never arrived" from "using it". */}
                      {!user.hasSignedIn && <span>never signed in</span>}
                    </p>
                  </div>

                  <span className="flex items-center gap-1.5 text-[11px]" style={{ color: status.color }}>
                    <span aria-hidden className="size-1.5 rounded-full" style={{ background: status.color }} />
                    {status.label}
                  </span>

                  {!isSelf && (
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: user.id,
                          changes: { status: user.status === 'suspended' ? 'active' : 'suspended' },
                        })
                      }
                      className="min-h-9 rounded-lg border px-3 text-xs"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      {user.status === 'suspended' ? 'Reactivate' : 'Deactivate'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-3 px-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
            Nobody is ever deleted — their cards and the context log would go with them.
            Deactivating locks someone out on their next request and keeps the record.
          </p>
        </section>
      </div>
      </div>
    </div>
  );
}
